import type { HTMLAttributes } from "react";

// Provider identity glyph for capability pickers (#1441). Self-contained (no
// remote assets — works air-gapped): renders the vendor brand mark when one is
// registered for the provider id, else a monogram tile derived from the
// provider's display label. One call site shape; providers without a mark fall
// back to the monogram, so new catalog providers never break.

// Registered brand marks, keyed by catalog `provider_id`
// (packages/foundation/.../persona_catalog.py). SINGLE SOURCE OF TRUTH for
// which providers show a logo. To add one: drop the asset under
// web/public/brand/providers/ and add an entry here — nothing else changes. Any
// provider absent from this map (e.g. openai_compatible) keeps the monogram
// fallback automatically.
interface ProviderBrandMark {
  /** Path under web/public (served at the site root). */
  src: string;
  /** Single-color mark that must invert on the dark theme to stay legible.
   *  Multicolor marks (Anthropic, Google) omit this so their colors survive. */
  monochrome?: boolean;
}

const PROVIDER_BRAND_MARKS: Readonly<Record<string, ProviderBrandMark>> = {
  openai: { src: "/brand/providers/openai.webp", monochrome: true },
  anthropic: { src: "/brand/providers/anthropic.webp" },
  deepgram: { src: "/brand/providers/deepgram.png", monochrome: true },
  elevenlabs: { src: "/brand/providers/elevenlabs.png", monochrome: true },
  google: { src: "/brand/providers/google.webp" },
  meta: { src: "/brand/providers/meta.svg", monochrome: true },
};

export interface ProviderLogoProps extends HTMLAttributes<HTMLSpanElement> {
  /** Catalog `provider_id`; selects the brand mark. Optional so callers with
   *  only a label still get the monogram fallback. */
  providerId?: string;
  label: string;
}

export function ProviderLogo({
  className,
  label,
  providerId,
  ...props
}: ProviderLogoProps) {
  const mark = providerId ? PROVIDER_BRAND_MARKS[providerId] : undefined;
  const classes = ["marker-provider-logo"];
  if (mark) {
    classes.push("marker-provider-logo--image");
    if (mark.monochrome) {
      classes.push("marker-provider-logo--mono");
    }
  }
  if (className) {
    classes.push(className);
  }
  return (
    <span aria-hidden="true" className={classes.join(" ")} {...props}>
      {mark ? (
        <img alt="" className="marker-provider-logo__img" src={mark.src} />
      ) : (
        monogram(label)
      )}
    </span>
  );
}

function monogram(label: string): string {
  const cleaned = label.trim();
  if (cleaned.length === 0) {
    return "?";
  }
  const words = cleaned.split(/[\s·-]+/).filter((word) => word.length > 0);
  if (words.length >= 2 && words[0] && words[1]) {
    return `${words[0][0] ?? ""}${words[1][0] ?? ""}`;
  }
  return cleaned.slice(0, 2);
}
